import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { DT } from '../theme/designTokens';
import { PageContent } from '../components/ui/PageContent';
import { StatBlock } from '../components/ui/StatBlock';
import { Divider } from '../components/ui/Divider';
import { apiClient } from '../network/ApiClient';
import type { StatsData, MatchHistoryEntry, PartnerStats } from '../types/game';
import { CountryFlag } from '../components/ui/CountryFlag';
import { PlayerAvatar } from '../components/ui/PlayerAvatar';
import { useTranslation } from '../i18n/react';
import { formatDate } from '../i18n/format';

// ── Fallback ──────────────────────────────────────────────────

const EMPTY_STATS: StatsData = {
  sessions_played: 0, sessions_completed: 0, sessions_won: 0, sessions_lost: 0,
  win_rate_all: null, win_rate_completed: null,
  forfeits: 0, abandonments: 0, abandoned_by_others: 0,
  rounds_played: 0, rounds_won: 0, rounds_lost: 0,
  points_for: 0, points_against: 0, average_points_for: null, average_points_against: null,
  tiles_played: 0, passes: 0, average_passes_per_session: null, average_rounds_per_session: null,
  blocked_rounds_won: 0, blocked_rounds_lost: 0,
  current_win_streak: 0, longest_win_streak: 0, current_loss_streak: 0, longest_loss_streak: 0,
};

// ── Primitives ────────────────────────────────────────────────

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize:      11,
        fontWeight:    700,
        fontFamily:    DT.font.sans,
        color:         DT.color.gold,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        marginBottom:  20,
      }}
    >
      {children}
    </div>
  );
}


/** Empty / loading placeholder */
function Placeholder({ message }: { message: string }) {
  return (
    <div
      style={{
        padding:    '24px 16px',
        textAlign:  'center',
        color:      DT.color.textMuted,
        fontSize:   13,
        fontFamily: DT.font.sans,
        borderTop:  `1px solid ${DT.color.border}`,
      }}
    >
      {message}
    </div>
  );
}

// ── Result badge ──────────────────────────────────────────────

const RESULT_STYLE: Record<string, { color: string; bg: string }> = {
  win:        { color: DT.color.gold,      bg: 'rgba(201,168,76,0.12)'  },
  loss:       { color: DT.color.teamRed,   bg: 'rgba(224,82,82,0.12)'   },
  forfeit:    { color: DT.color.textMuted, bg: 'rgba(168,159,134,0.08)' },
  no_contest: { color: DT.color.textMuted, bg: 'rgba(168,159,134,0.08)' },
};

function ResultBadge({ result }: { result: string }) {
  const { t } = useTranslation('stats');
  // Known result enums get localized labels; an unknown future backend value
  // falls back to the raw value uppercased (never parsed or guessed).
  const label =
    result === 'win'        ? t('result.win')
    : result === 'loss'       ? t('result.loss')
    : result === 'forfeit'    ? t('result.forfeit')
    : result === 'no_contest' ? t('result.noContest')
    : result.toUpperCase();
  const meta = { label, ...(RESULT_STYLE[result] ?? { color: DT.color.textMuted, bg: 'rgba(168,159,134,0.08)' }) };
  return (
    <span
      style={{
        display:       'inline-block',
        fontSize:      9,
        fontWeight:    700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color:         meta.color,
        fontFamily:    DT.font.sans,
        whiteSpace:    'nowrap',
        background:    meta.bg,
        borderRadius:  DT.radius.sm,
        padding:       '2px 6px',
      }}
    >
      {meta.label}
    </span>
  );
}

// ── Table column header row ───────────────────────────────────

function ColHeader({ children, align = 'left' }: { children: ReactNode; align?: React.CSSProperties['textAlign'] }) {
  return (
    <span
      style={{
        fontSize:      10,
        fontWeight:    700,
        letterSpacing: '0.11em',
        textTransform: 'uppercase',
        color:         DT.color.textMuted,
        fontFamily:    DT.font.sans,
        textAlign:     align,
      }}
    >
      {children}
    </span>
  );
}

// ── Match history ─────────────────────────────────────────────

function MatchHistorySection({ loading, history }: { loading: boolean; history: MatchHistoryEntry[] | null }) {
  const { t } = useTranslation('stats');
  if (loading) return <Placeholder message={t('loading')} />;
  if (!history || history.length === 0) return <Placeholder message={t('history.empty')} />;

  const entries = history.slice(0, 15);

  return (
    <div>
      {/* Table header — desktop only */}
      <div
        className="hidden lg:grid"
        style={{
          gridTemplateColumns: '100px 90px 1fr 70px 100px',
          gap:                 16,
          padding:             '0 0 10px',
          borderBottom:        `1px solid ${DT.color.border}`,
          marginBottom:        6,
        }}
      >
        <ColHeader>{t('history.colResult')}</ColHeader>
        <ColHeader>{t('history.colScore')}</ColHeader>
        <ColHeader>{t('history.colPartner')}</ColHeader>
        <ColHeader align="center">{t('history.colRounds')}</ColHeader>
        <ColHeader align="right">{t('history.colDate')}</ColHeader>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {entries.map(entry => (
          <MatchRow key={entry.session_id} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function MatchRow({ entry }: { entry: MatchHistoryEntry }) {
  const { t } = useTranslation('stats');
  const date    = formatDate(entry.finished_at, { month: 'short', day: 'numeric', year: 'numeric' });
  const partner = entry.partner?.display_name ?? '—';
  const score   = `${entry.points_for} – ${entry.points_against}`;
  const isWin   = entry.result === 'win';
  const rowBg   = isWin ? 'rgba(201,168,76,0.04)' : 'transparent';
  const border  = isWin ? `1px solid rgba(201,168,76,0.15)` : `1px solid transparent`;

  return (
    <>
      {/* Desktop row — 5-column grid */}
      <div
        className="hidden lg:grid"
        style={{
          gridTemplateColumns: '100px 90px 1fr 70px 100px',
          alignItems:          'center',
          gap:                 16,
          padding:             '11px 10px',
          background:          rowBg,
          border,
          borderRadius:        DT.radius.sm,
        }}
      >
        <ResultBadge result={entry.result} />
        <span
          style={{
            fontSize:   18,
            fontWeight: 400,
            color:      isWin ? DT.color.gold : DT.color.textPrimary,
            fontFamily: DT.font.heading,
          }}
        >
          {score}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {/* Player Identity V1: flag beside the partner name (humans only). */}
          {entry.partner && !entry.partner.is_bot && (
            <CountryFlag countryCode={entry.partner.country_code} width={16} decorative />
          )}
          <span
            title={entry.partner ? partner : undefined}
            style={{
              fontSize:     13,
              color:        DT.color.textSecondary,
              fontFamily:   DT.font.sans,
              overflow:     'hidden',
              textOverflow: 'ellipsis',
              whiteSpace:   'nowrap',
            }}
          >
            {t('history.withPartner', { name: partner })}{entry.vs_bots ? ' 🤖' : ''}
          </span>
        </span>
        <span style={{ fontSize: 12, color: DT.color.textMuted, fontFamily: DT.font.sans, textAlign: 'center' }}>
          {t('history.roundsShort', { n: entry.rounds_count })}
        </span>
        <span style={{ fontSize: 12, color: DT.color.textMuted, fontFamily: DT.font.sans, textAlign: 'right' }}>
          {date}
        </span>
      </div>

      {/* Mobile row — compact flex */}
      <div
        className="flex lg:hidden"
        style={{
          alignItems: 'center',
          gap:        12,
          padding:    '12px 0',
          borderBottom: `1px solid ${DT.color.border}`,
          background:   rowBg,
        }}
      >
        <div style={{ minWidth: 68 }}>
          <ResultBadge result={entry.result} />
          <div
            style={{
              fontSize:   18,
              fontWeight: 400,
              color:      isWin ? DT.color.gold : DT.color.textPrimary,
              fontFamily: DT.font.heading,
              lineHeight: 1.2,
              marginTop:  2,
            }}
          >
            {score}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {entry.partner && !entry.partner.is_bot && (
              <CountryFlag countryCode={entry.partner.country_code} width={16} decorative />
            )}
            <span
              title={entry.partner ? partner : undefined}
              style={{
                fontSize:     13,
                fontWeight:   700,
                color:        DT.color.textPrimary,
                fontFamily:   DT.font.sans,
                whiteSpace:   'nowrap',
                overflow:     'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('history.withPartner', { name: partner })}{entry.vs_bots ? ' 🤖' : ''}
            </span>
          </div>
          <div style={{ fontSize: 11, color: DT.color.textMuted, fontFamily: DT.font.sans, marginTop: 2 }}>
            {t('history.rounds', { count: entry.rounds_count })}
          </div>
        </div>
        <div style={{ fontSize: 11, color: DT.color.textMuted, fontFamily: DT.font.sans, flexShrink: 0 }}>
          {date}
        </div>
      </div>
    </>
  );
}

// ── Partner stats ─────────────────────────────────────────────

function pickHighlights(partners: PartnerStats[]) {
  const mostPlayed = partners[0] ?? null;
  const withRate   = partners.filter(p => p.completed_together > 0 && p.win_rate_together != null);
  const best       = withRate.length > 0
    ? withRate.reduce((a, b) => b.win_rate_together! > a.win_rate_together! ? b : a)
    : null;
  return { mostPlayed, best };
}

function PartnerHighlights({ partners }: { partners: PartnerStats[] }) {
  const { t } = useTranslation('stats');
  const { mostPlayed, best } = pickHighlights(partners);
  if (!mostPlayed) return null;

  const chipStyle: React.CSSProperties = {
    background:   DT.color.cardBg,
    border:       `1px solid ${DT.color.border}`,
    borderRadius: DT.radius.md,
    padding:      '16px 18px',
    flex:         '1 1 0',
  };

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
      <div style={chipStyle}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: DT.color.textMuted, fontFamily: DT.font.sans, marginBottom: 6 }}>
          {t('partners.mostPlayed')}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: DT.color.textPrimary, fontFamily: DT.font.sans, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {mostPlayed.partner_display_name}{mostPlayed.is_bot ? ' 🤖' : ''}
        </div>
        <div style={{ fontSize: 12, color: DT.color.gold, fontFamily: DT.font.sans, marginTop: 4 }}>
          {t('partners.sessions', { count: mostPlayed.sessions_together })}
        </div>
      </div>

      <div style={chipStyle}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: DT.color.textMuted, fontFamily: DT.font.sans, marginBottom: 6 }}>
          {t('partners.bestPartner')}
        </div>
        {best ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: DT.color.textPrimary, fontFamily: DT.font.sans, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {best.partner_display_name}{best.is_bot ? ' 🤖' : ''}
            </div>
            <div style={{ fontSize: 12, color: DT.color.gold, fontFamily: DT.font.sans, marginTop: 4 }}>
              {t('partners.winRateValue', { value: best.win_rate_together })}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: DT.color.textMuted, fontFamily: DT.font.sans }}>—</div>
            <div style={{ fontSize: 12, color: DT.color.textMuted, fontFamily: DT.font.sans, marginTop: 4 }}>
              {t('partners.needCompleted')}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PartnersSection({ loading, partners }: { loading: boolean; partners: PartnerStats[] | null }) {
  const { t } = useTranslation('stats');
  if (loading) return <Placeholder message={t('loading')} />;
  if (!partners || partners.length === 0) return <Placeholder message={t('partners.empty')} />;

  return (
    <div>
      <PartnerHighlights partners={partners} />

      {/* Table header — desktop only */}
      <div
        className="hidden lg:grid"
        style={{
          gridTemplateColumns: '1fr 80px 56px 56px 80px 80px',
          gap:                 16,
          padding:             '0 0 10px',
          borderBottom:        `1px solid ${DT.color.border}`,
          marginBottom:        6,
        }}
      >
        <ColHeader>{t('partners.colPartner')}</ColHeader>
        <ColHeader align="center">{t('partners.colSessions')}</ColHeader>
        <ColHeader align="center">{t('partners.colW')}</ColHeader>
        <ColHeader align="center">{t('partners.colL')}</ColHeader>
        <ColHeader align="center">{t('partners.colWinRate')}</ColHeader>
        <ColHeader align="right">{t('partners.colNoContest')}</ColHeader>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {partners.map(p => (
          <PartnerRow key={p.partner_user_id} p={p} />
        ))}
      </div>
    </div>
  );
}

function PartnerRow({ p }: { p: PartnerStats }) {
  const { t } = useTranslation('stats');
  const wr = p.win_rate_together != null ? `${p.win_rate_together}%` : '—';

  return (
    <>
      {/* Desktop row — 6-column grid */}
      <div
        className="hidden lg:grid"
        style={{
          gridTemplateColumns: '1fr 80px 56px 56px 80px 80px',
          alignItems:          'center',
          gap:                 16,
          padding:             '11px 10px',
          borderRadius:        DT.radius.sm,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {/* Player Identity V1: flag (humans only) + avatar + full name. */}
          {!p.is_bot && <CountryFlag countryCode={p.partner_country_code} width={18} decorative />}
          <PlayerAvatar avatarKey={p.partner_avatar_key} name={p.partner_display_name} size={22} />
          <span title={p.partner_display_name} style={{ fontSize: 13, fontWeight: 700, color: DT.color.textPrimary, fontFamily: DT.font.sans, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.partner_display_name}{p.is_bot ? ' 🤖' : ''}
          </span>
        </span>
        <span style={{ fontSize: 12, color: DT.color.textMuted, fontFamily: DT.font.sans, textAlign: 'center' }}>
          {p.sessions_together}
        </span>
        <span style={{ fontSize: 15, fontWeight: 400, color: DT.color.gold, fontFamily: DT.font.heading, textAlign: 'center' }}>
          {p.wins_together}
        </span>
        <span style={{ fontSize: 15, fontWeight: 400, color: DT.color.textPrimary, fontFamily: DT.font.heading, textAlign: 'center' }}>
          {p.losses_together}
        </span>
        <span style={{ fontSize: 15, fontWeight: 400, color: DT.color.gold, fontFamily: DT.font.heading, textAlign: 'center' }}>
          {wr}
        </span>
        <span style={{ fontSize: 12, color: DT.color.textMuted, fontFamily: DT.font.sans, textAlign: 'right' }}>
          {p.abandoned_together > 0 ? t('partners.noContestCount', { n: p.abandoned_together }) : '—'}
        </span>
      </div>

      {/* Mobile row */}
      <div
        className="flex lg:hidden"
        style={{
          alignItems:   'center',
          gap:          12,
          padding:      '12px 0',
          borderBottom: `1px solid ${DT.color.border}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {!p.is_bot && <CountryFlag countryCode={p.partner_country_code} width={16} decorative />}
            <span title={p.partner_display_name} style={{ fontSize: 13, fontWeight: 700, color: DT.color.textPrimary, fontFamily: DT.font.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.partner_display_name}{p.is_bot ? ' 🤖' : ''}
            </span>
          </div>
          <div style={{ fontSize: 11, color: DT.color.textMuted, fontFamily: DT.font.sans, marginTop: 2 }}>
            {t('partners.sessions', { count: p.sessions_together })}
            {p.abandoned_together > 0 ? ` · ${t('partners.noContestCount', { n: p.abandoned_together })}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, flexShrink: 0, textAlign: 'right' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 400, color: DT.color.gold, fontFamily: DT.font.heading, lineHeight: 1 }}>{p.wins_together}</div>
            <div style={{ fontSize: 9, color: DT.color.textMuted, fontFamily: DT.font.sans, letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>{t('partners.shortW')}</div>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 400, color: DT.color.textPrimary, fontFamily: DT.font.heading, lineHeight: 1 }}>{p.losses_together}</div>
            <div style={{ fontSize: 9, color: DT.color.textMuted, fontFamily: DT.font.sans, letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>{t('partners.shortL')}</div>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 400, color: DT.color.gold, fontFamily: DT.font.heading, lineHeight: 1 }}>{wr}</div>
            <div style={{ fontSize: 9, color: DT.color.textMuted, fontFamily: DT.font.sans, letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>{t('partners.shortWR')}</div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Screen ────────────────────────────────────────────────────

export function StatsScreen() {
  const { t } = useTranslation('stats');
  const [stats,    setStats]    = useState<StatsData | null>(null);
  const [history,  setHistory]  = useState<MatchHistoryEntry[] | null>(null);
  const [partners, setPartners] = useState<PartnerStats[] | null>(null);
  const [loadingStats,    setLoadingStats]    = useState(true);
  const [loadingHistory,  setLoadingHistory]  = useState(true);
  const [loadingPartners, setLoadingPartners] = useState(true);

  useEffect(() => {
    apiClient.getStats()
      .then(setStats)
      .catch(() => setStats(EMPTY_STATS))
      .finally(() => setLoadingStats(false));

    apiClient.getMatchHistory()
      .then(r => setHistory(r.history))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));

    apiClient.getPartnerStats()
      .then(r => setPartners(r.partners))
      .catch(() => setPartners([]))
      .finally(() => setLoadingPartners(false));
  }, []);

  const s   = stats ?? EMPTY_STATS;
  const dsh = loadingStats ? '—' : undefined;
  const wr  = (r: number | null) => r != null ? `${r}%` : '—';
  const num = (n: number) => loadingStats ? '—' : n;

  const curStreak = loadingStats ? '—'
    : s.current_win_streak > 0  ? t('streakWins', { n: s.current_win_streak })
    : s.current_loss_streak > 0 ? t('streakLosses', { n: s.current_loss_streak })
    : '—';

  return (
    <div style={{ width: '100%', minHeight: '100%', background: DT.color.pageBg, paddingBottom: 80 }}>
      <PageContent>

        {/* ── Page heading ── */}
        <div style={{ paddingTop: 40, paddingBottom: 32 }}>
          <h1
            style={{
              fontSize:   'clamp(28px, 3.5vw, 40px)',
              fontWeight: 400,
              fontFamily: DT.font.heading,
              color:      DT.color.textPrimary,
              margin:     0,
              lineHeight: 1.1,
            }}
          >
            {t('title')}
          </h1>
          <p
            style={{
              marginTop:  8,
              fontSize:   15,
              color:      DT.color.textMuted,
              fontFamily: DT.font.sans,
              margin:     '8px 0 0',
            }}
          >
            {t('subtitle')}
          </p>
        </div>

        {/* ── Sessions ── */}
        <section>
          <Eyebrow>{t('section.sessions')}</Eyebrow>
          <div style={{ background: DT.color.cardBg, border: `1px solid ${DT.color.border}`, borderRadius: DT.radius.xl, padding: '20px 20px' }}>
            <div className="grid grid-cols-2 lg:grid-cols-4" style={{ gap: 'clamp(16px, 4vw, 32px)' }}>
              <StatBlock label={t('metric.played')}           value={dsh ?? num(s.sessions_played)} />
              <StatBlock label={t('metric.wins')}             value={dsh ?? num(s.sessions_won)}    gold />
              <StatBlock label={t('metric.losses')}           value={dsh ?? num(s.sessions_lost)} />
              <StatBlock label={t('metric.winRateAll')}       value={dsh ?? wr(s.win_rate_all)}    gold />
              <StatBlock label={t('metric.winRateCompleted')} value={dsh ?? wr(s.win_rate_completed)} gold />
              <StatBlock label={t('metric.forfeits')}         value={dsh ?? num(s.forfeits)} />
              <StatBlock label={t('metric.partnerForfeits')}  value={dsh ?? num(s.abandoned_by_others)} />
              <StatBlock label={t('metric.curStreak')}        value={dsh ?? curStreak}             gold={s.current_win_streak > 0} />
            </div>
          </div>
        </section>

        <Divider margin="40px 0" />

        {/* ── Rounds & Gameplay ── */}
        <section>
          <Eyebrow>{t('section.rounds')}</Eyebrow>
          <div style={{ background: DT.color.cardBg, border: `1px solid ${DT.color.border}`, borderRadius: DT.radius.xl, padding: '20px 20px' }}>
            <div className="grid grid-cols-2 lg:grid-cols-4" style={{ gap: 'clamp(16px, 4vw, 32px)' }}>
              <StatBlock label={t('metric.roundsPlayed')}        value={dsh ?? num(s.rounds_played)} />
              <StatBlock label={t('metric.roundsWon')}           value={dsh ?? num(s.rounds_won)}    gold />
              <StatBlock label={t('metric.roundsLost')}          value={dsh ?? num(s.rounds_lost)} />
              <StatBlock label={t('metric.pointsFor')}           value={dsh ?? num(s.points_for)}    gold />
              <StatBlock label={t('metric.pointsAgainst')}       value={dsh ?? num(s.points_against)} />
              <StatBlock label={t('metric.tilesPlayed')}         value={dsh ?? num(s.tiles_played)} />
              <StatBlock label={t('metric.passes')}              value={dsh ?? num(s.passes)} />
              <StatBlock label={t('metric.avgRoundsPerSession')} value={dsh ?? (s.average_rounds_per_session != null ? s.average_rounds_per_session : '—')} />
              <StatBlock label={t('metric.avgPtsPerSession')}    value={dsh ?? (s.average_points_for != null ? s.average_points_for : '—')} gold />
              <StatBlock label={t('metric.longestWinStreak')}    value={dsh ?? num(s.longest_win_streak)} gold />
            </div>
          </div>
        </section>

        <Divider margin="40px 0" />

        {/* ── Recent Matches ── */}
        <section>
          <Eyebrow>{t('section.recentMatches')}</Eyebrow>
          <MatchHistorySection loading={loadingHistory} history={history} />
        </section>

        <Divider margin="40px 0" />

        {/* ── Partners ── */}
        <section>
          <Eyebrow>{t('section.partners')}</Eyebrow>
          <PartnersSection loading={loadingPartners} partners={partners} />
        </section>

      </PageContent>
    </div>
  );
}
